import React, { useState } from "react";
import { toast } from "sonner";
import { History, Plus, Trash2, Pencil } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const EMPTY = { label: "", start_year: "", end_year: "", members: "", note: "" };
const toLines = (m) => (m || []).map((x) => `${x.title} — ${x.name}`).join("\n");
const parse = (s) => s.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [title, ...rest] = l.split(/\s[—–-]\s|:\s/); return { title: title.trim(), name: rest.join(" ").trim() || "—" }; });

function TermForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { label: f.label || `${f.start_year}${f.end_year ? ` – ${f.end_year}` : ""}`, start_year: parseInt(f.start_year), end_year: parseInt(f.end_year) || 0, members: parse(f.members), note: f.note };
    try {
      if (f.id) await api.put(`/admin/governance/history/${f.id}`, body); else await api.post("/admin/governance/history", body);
      toast.success("Board term saved"); onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3 rounded-xl border border-gold/40 bg-gold-light/40 p-4" data-testid="term-form">
      <div className="grid gap-3 sm:grid-cols-3">
        <div><Label>Start year</Label><Input type="number" value={f.start_year} onChange={set("start_year")} data-testid="term-start" /></div>
        <div><Label>End year</Label><Input type="number" value={f.end_year} onChange={set("end_year")} data-testid="term-end" /></div>
        <div><Label>Label (optional)</Label><Input placeholder="2022 – 2024" value={f.label} onChange={set("label")} data-testid="term-label" /></div>
      </div>
      <div><Label>Board members — one per line as "Title — Name"</Label><Textarea rows={6} placeholder={"President — Nana Kwame Mensah\nVice President — Akosua Boateng\nTreasurer — Yaw Ofori"} value={f.members} onChange={set("members")} data-testid="term-members" /></div>
      <div><Label>Highlights / note</Label><Textarea rows={2} placeholder="Key achievements during this term" value={f.note} onChange={set("note")} data-testid="term-note" /></div>
      <div className="flex gap-2">
        <Button onClick={save} disabled={!f.start_year} className="flex-1 bg-crimson" data-testid="term-save">{f.id ? "Save changes" : "Add term"}</Button>
        <Button variant="ghost" onClick={onCancel} data-testid="term-cancel">Cancel</Button>
      </div>
    </div>
  );
}

export function BoardHistoryAdmin() {
  const { data, reload } = useApi("/governance/history");
  const [editing, setEditing] = useState(null);
  const remove = async (t) => {
    if (!window.confirm(`Delete the ${t.label} board?`)) return;
    try { await api.delete(`/admin/governance/history/${t.id}`); toast.success("Term deleted"); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="glass mt-8 rounded-2xl p-6 space-y-3" data-testid="board-history-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-serif-display text-xl font-bold text-crimson"><History size={18} className="text-gold" /> Past executive boards</h3>
          <p className="text-xs text-muted-foreground">Shown as a timeline on the public Leadership page.</p>
        </div>
        {!editing && <Button size="sm" onClick={() => setEditing(EMPTY)} className="bg-crimson" data-testid="term-new"><Plus size={15} className="mr-1" /> Add term</Button>}
      </div>
      {editing && <TermForm initial={editing} onSaved={() => { setEditing(null); reload(); }} onCancel={() => setEditing(null)} />}
      <div className="space-y-2">
        {(data || []).map((t) => (
          <div key={t.id} className="flex items-start gap-3 rounded-xl border border-gold/30 bg-parchment-card p-3" data-testid={`term-row-${t.id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{t.label} <span className="text-xs text-muted-foreground">· {t.members.length} members</span></p>
              <p className="truncate text-xs text-muted-foreground">{t.members.map((m) => `${m.title}: ${m.name}`).join(" · ")}</p>
            </div>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing({ ...t, members: toLines(t.members), end_year: t.end_year || "" })} data-testid={`term-edit-${t.id}`}><Pencil size={14} /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(t)} data-testid={`term-delete-${t.id}`}><Trash2 size={14} /></Button>
          </div>
        ))}
        {data && data.length === 0 && <p className="text-sm text-muted-foreground" data-testid="term-empty">No past boards recorded yet.</p>}
      </div>
    </div>
  );
}
